import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import useCRUD from "../hooks/useCRUD";
import Spinner from "../components/Spinner/Spinner";
import { formatDateWithDay } from "../utils/dateUtils";

const PublicEventRsvp = () => {
  const { eventCode } = useParams();
  const { data, fetchItems, isLoading, error } = useCRUD(
    `/api/event-participations/public/${eventCode}`
  );

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const participants = useMemo(
    () => (data?.eventParticipations || []).filter((ep) => !ep.isDriver),
    [data]
  );

  const sortedParticipants = useMemo(() => {
    const sortable = [...participants];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const aVal = (a[sortConfig.key] || "").toString().toLowerCase();
        const bVal = (b[sortConfig.key] || "").toString().toLowerCase();
        if (aVal < bVal) return sortConfig.direction === "ascending" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [participants, sortConfig]);

  if (isLoading) return <Spinner />;
  if (error || !data)
    return <div className="text-center text-red-500">Invalid Request</div>;

  const { eventDetails, eventParticipations } = data;
  const isRSVPOnly = eventDetails.eventSignUpType === "RSVP Only";

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  let rideRequests = 0;
  let driversAvailable = 0;
  let rsvpYesCount = 0;
  let pendingParticipantsCount = 0;

  eventParticipations.forEach((ep) => {
    const count = Number(ep.noOfPpl) || 1;

    if (!ep.isDriver) {
      if (ep.needRide === "Yes") {
        rideRequests += count;
        rsvpYesCount += count;
      } else if (ep.needRide === "Pending") {
        pendingParticipantsCount += count;
      }
    } else if (ep.availability === "Yes") {
      driversAvailable += 1;
    }
  });

  const drivers = eventParticipations.filter((ep) => ep.isDriver);

  const getRsvpBadgeClass = (status) => {
    if (status === "Yes") return "badge badge-success text-white";
    if (status === "No") return "badge badge-error text-white";
    return "badge badge-warning text-white";
  };

  return (
    <div className="container mx-auto p-4">
      <h2 className="text-2xl font-bold mb-2">{eventDetails.title}</h2>
      <p className="mb-4">
        {formatDateWithDay(eventDetails.dateOfOccurrence)} |{" "}
        {eventDetails.location}
      </p>

      <div
        className={`grid grid-cols-1 ${
          isRSVPOnly ? "sm:grid-cols-2" : "sm:grid-cols-3"
        } gap-4 mb-4`}
      >
        {isRSVPOnly ? (
          <>
            <div className="stats shadow">
              <div className="stat">
                <div className="stat-title">Confirmed Participants</div>
                <div className="stat-value text-green-700">{rsvpYesCount}</div>
              </div>
            </div>
            <div className="stats shadow">
              <div className="stat">
                <div className="stat-title">Pending Participants</div>
                <div className="stat-value text-gray-500">
                  {pendingParticipantsCount}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="stats shadow">
              <div className="stat">
                <div className="stat-title">Need Rides</div>
                <div className="stat-value text-blue-600">{rideRequests}</div>
              </div>
            </div>
            <div className="stats shadow">
              <div className="stat">
                <div className="stat-title">Drivers Available</div>
                <div className="stat-value text-yellow-500">
                  {driversAvailable}
                </div>
              </div>
            </div>
            <div className="stats shadow">
              <div className="stat">
                <div className="stat-title">Pending Participants</div>
                <div className="stat-value text-gray-500">
                  {pendingParticipantsCount}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {isRSVPOnly ? (
        <div className="overflow-x-auto mb-8">
          <h3 className="text-lg font-semibold mb-2">Participants</h3>
          <table className="table-auto w-full border">
            <thead>
              <tr className="bg-gray-100">
                <th
                  className="px-2 py-1 border cursor-pointer"
                  onClick={() => requestSort("name")}
                >
                  Name
                </th>
                <th
                  className="px-2 py-1 border cursor-pointer"
                  onClick={() => requestSort("needRide")}
                >
                  RSVP
                </th>
                <th className="px-2 py-1 border">Message to Admin</th>
              </tr>
            </thead>
            <tbody>
              {sortedParticipants.map((ep) => (
                <tr key={ep._id} className="text-center">
                  <td className="border px-2 py-1">{ep.name}</td>
                  <td className="border px-2 py-1">
                    <span className={getRsvpBadgeClass(ep.needRide)}>
                      {ep.needRide}
                    </span>
                  </td>
                  <td className="border px-2 py-1">
                    {ep.messageToAdmin ? ep.messageToAdmin : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto mb-8">
            <h3 className="text-lg font-semibold mb-2">Participants</h3>
            <table className="table-auto w-full border">
              <thead>
                <tr className="bg-gray-100">
                  <th
                    className="px-2 py-1 border cursor-pointer"
                    onClick={() => requestSort("name")}
                  >
                    Name
                  </th>
                  <th className="px-2 py-1 border">Ride Location</th>
                  <th
                    className="px-2 py-1 border cursor-pointer"
                    onClick={() => requestSort("needRide")}
                  >
                    Needs Ride
                  </th>
                  <th className="px-2 py-1 border">Message to Admin</th>
                </tr>
              </thead>
              <tbody>
                {sortedParticipants.map((ep) => (
                  <tr key={ep._id} className="text-center">
                    <td className="border px-2 py-1">{ep.name}</td>
                    <td className="border px-2 py-1">{ep.rideLocation}</td>
                    <td className="border px-2 py-1">
                      <span className={getRsvpBadgeClass(ep.needRide)}>
                        {ep.needRide}
                      </span>
                    </td>
                    <td className="border px-2 py-1">
                      {ep.messageToAdmin ? ep.messageToAdmin : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="overflow-x-auto">
            <h3 className="text-lg font-semibold mb-2">Drivers</h3>
            <table className="table-auto w-full border">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-2 py-1 border">Name</th>
                  <th className="px-2 py-1 border">Availability</th>
                  <th className="px-2 py-1 border">Can Take</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((driver) => (
                  <tr key={driver._id} className="text-center">
                    <td className="border px-2 py-1">{driver.name}</td>
                    <td className="border px-2 py-1">{driver.availability}</td>
                    <td className="border px-2 py-1">{driver.canTake}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default PublicEventRsvp;
